import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { toast } from "sonner"
import { useTranslation } from "@/hooks/use-translation"
import { GameLayout } from '@/components/games/common/game-layout'
import { GameHistoryCalendar } from '@/components/games/common/game-history-calendar'
import { Trophy, Download, Upload, BarChart3, Trash2, Play } from 'lucide-react'
import { getUserStats, exportStats, importStats, clearOldData } from '@/lib/game-stats'
import { getAvailableGames, getLocalizedGameId } from '@/lib/games-data'
import { InContentAd } from '@/components/adsense'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

interface GameStats {
  totalGamesPlayed: number
  totalGamesWon: number
  maxStreak: number
  currentStreak: number
  lastPlayed?: string
}

export function StatisticsPage() {
  const { t, language, navigate: localizedNavigate } = useTranslation()
  const [stats, setStats] = useState(() => {
    const availableGames = getAvailableGames(language)
    const gameStats: { [gameId: string]: GameStats } = {}

    availableGames.forEach(game => {
      const gameKey = `${game.id}-${language}`
      gameStats[game.id] = getUserStats(gameKey)
    })

    return gameStats
  })

  // Función para refrescar las estadísticas
  const refreshStats = () => {
    const availableGames = getAvailableGames(language)
    const gameStats: { [gameId: string]: GameStats } = {}

    availableGames.forEach(game => {
      const gameKey = `${game.id}-${language}`
      gameStats[game.id] = getUserStats(gameKey)
    })

    setStats(gameStats)
  }

  // Refrescar estadísticas cuando cambie el idioma o se monte el componente
  useEffect(() => {
    refreshStats()
  }, [language])

  // Refrescar estadísticas cuando la ventana regains focus (usuario regresa de otro juego)
  useEffect(() => {
    const handleFocus = () => {
      refreshStats()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [language])

  const handleExportAllStats = () => {
    const availableGames = getAvailableGames(language)
    const allStats: Record<string, unknown> = {}

    availableGames.forEach(game => {
      const gameKey = `${game.id}-${language}`
      const data = exportStats(gameKey)
      allStats[game.id] = JSON.parse(data)
    })

    const blob = new Blob([JSON.stringify(allStats, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `retos-diarios-stats-${language}-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(t("statistics.exportSuccess"))
  }

  const handleImportAllStats = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const data = e.target?.result as string
          try {
            const allStats = JSON.parse(data)
            const availableGames = getAvailableGames(language)
            let successCount = 0

            availableGames.forEach(game => {
              if (allStats[game.id]) {
                const success = importStats(JSON.stringify(allStats[game.id]))
                if (success) successCount++
              }
            })

            if (successCount > 0) {
              // Recargar estadísticas
              refreshStats()
              toast.success(t("statistics.importSuccess", { count: successCount }))
            } else {
              toast.error(t("statistics.importError"))
            }
          } catch {
            toast.error(t("statistics.importError"))
          }
        }
        reader.readAsText(file)
      }
    }
    input.click()
  }

  const handleClearAllData = () => {
    clearOldData()
    toast.success(t("statistics.clearAllDataSuccess"))
  }

  const renderOverviewContent = () => {
    const availableGames = getAvailableGames(language)
    const gamesWithActivity = availableGames.filter(game => {
      const gameStats = stats[game.id]
      return gameStats.totalGamesPlayed > 0
    })
    const gamesWithoutActivity = availableGames.filter(game => {
      const gameStats = stats[game.id]
      return gameStats.totalGamesPlayed === 0
    })

    const totalGames = Object.values(stats).reduce((sum, game) => sum + game.totalGamesPlayed, 0)
    const totalWon = Object.values(stats).reduce((sum, game) => sum + game.totalGamesWon, 0)
    const winRate = totalGames > 0 ? Math.round((totalWon / totalGames) * 100) : 0
    const maxStreak = Math.max(...Object.values(stats).map(game => game.maxStreak), 0)

    const handleGameClick = (gameId: string) => {
      const localizedId = getLocalizedGameId(gameId, language)
      localizedNavigate(`/game/${localizedId}`)
    }

    return (
      <div className="w-full space-y-4">
        {/* Resumen general con métricas destacadas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4 pb-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t("statistics.totalGames")}</p>
                <p className="text-2xl font-bold text-blue-600">{totalGames}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4 pb-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t("statistics.gamesWon")}</p>
                <p className="text-2xl font-bold text-green-600">{totalWon}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-4 pb-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t("statistics.winRate")}</p>
                <p className="text-2xl font-bold text-yellow-600">{winRate}%</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-4 pb-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t("statistics.maxStreak")}</p>
                <p className="text-2xl font-bold text-purple-600">{maxStreak}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Juegos con actividad - Vista detallada */}
        {gamesWithActivity.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-yellow-600" />
              <h2 className="text-base font-semibold">{t("statistics.gamesWithActivity") || "Juegos con Actividad"}</h2>
              <span className="text-xs text-muted-foreground">({gamesWithActivity.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {gamesWithActivity.map((game) => {
                const gameStats = stats[game.id]
                const winRate = gameStats.totalGamesPlayed > 0
                  ? Math.round((gameStats.totalGamesWon / gameStats.totalGamesPlayed) * 100)
                  : 0
                return (
                  <Card key={game.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-sm">
                        <game.icon className="h-4 w-4 text-primary" />
                        {t(`games.${game.id}.name`)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          <div className="text-center">
                            <div className="text-xl font-bold">{gameStats.totalGamesPlayed}</div>
                            <div className="text-xs text-muted-foreground">{t("statistics.played")}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-green-600">{gameStats.totalGamesWon}</div>
                            <div className="text-xs text-muted-foreground">{t("statistics.won")}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-purple-600">{gameStats.maxStreak}</div>
                            <div className="text-xs text-muted-foreground">{t("statistics.maxStreak")}</div>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">{t("statistics.winRate")}</span>
                            <span className="font-semibold">{winRate}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div 
                              className="bg-green-500 h-1.5 rounded-full transition-all"
                              style={{ width: `${winRate}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
        )}

        {/* Juegos sin actividad - mismo formato que juegos con actividad */}
        {gamesWithoutActivity.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-base font-semibold text-muted-foreground">
                {t("statistics.gamesWithoutActivity") || "Otros Juegos"}
              </h2>
              <span className="text-xs text-muted-foreground">({gamesWithoutActivity.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {gamesWithoutActivity.map((game) => (
                <Card key={game.id} className="opacity-75 hover:opacity-100 transition-opacity">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <game.icon className="h-4 w-4 text-primary" />
                      {t(`games.${game.id}.name`)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center">
                          <div className="text-xl font-bold text-muted-foreground">-</div>
                          <div className="text-xs text-muted-foreground">{t("statistics.played")}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-muted-foreground">-</div>
                          <div className="text-xs text-muted-foreground">{t("statistics.won")}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-muted-foreground">-</div>
                          <div className="text-xs text-muted-foreground">{t("statistics.maxStreak")}</div>
                        </div>
                      </div>
                      <Button 
                        onClick={() => handleGameClick(game.id)}
                        className="w-full"
                        size="sm"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        {t("statistics.playNow") || "Jugar ahora"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderActionsContent = () => (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            {t("statistics.dataManagement")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={handleExportAllStats} variant="outline" className="w-full">
            <Download className="h-4 w-4 mr-2" />
            {t("statistics.exportAllStats")}
          </Button>
          <Button onClick={handleImportAllStats} variant="outline" className="w-full">
            <Upload className="h-4 w-4 mr-2" />
            {t("statistics.importAllStats")}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="w-full">
                <Trash2 className="h-4 w-4 mr-2" />
                {t("statistics.clearAllData")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("statistics.clearAllData")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("statistics.clearAllDataConfirm")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("navigation.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearAllData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  {t("statistics.clearAllData")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  )

  return (
    <GameLayout>
      {/* Desktop Layout */}
      <div className="hidden lg:flex lg:flex-row lg:gap-8 lg:px-8 lg:py-8">
        <div className="flex-1">
          <Tabs defaultValue="overview" className="w-full">
            <div className="w-full max-w-6xl mx-auto mb-8">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">
                  {t("statistics.overview")}
                </TabsTrigger>
                <TabsTrigger value="calendar">
                  {t("statistics.calendar")}
                </TabsTrigger>
                <TabsTrigger value="actions">
                  {t("statistics.actions")}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview">
              {renderOverviewContent()}
            </TabsContent>

            <TabsContent value="calendar">
              <div className="w-full max-w-6xl mx-auto space-y-6">
                <GameHistoryCalendar language={language} />
              </div>
            </TabsContent>

            <TabsContent value="actions">
              {renderActionsContent()}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar - Desktop only */}
        <div className="w-80 min-w-80 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Trophy className="h-4 w-4" />
                {t("statistics.about")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs">{t("statistics.description")}</p>
            </CardContent>
          </Card>

          {/* Advertisement */}
          <InContentAd className="w-full" compact />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden h-[calc(100dvh-64px)] flex flex-col">
        <div className="p-4 flex-1 overflow-auto">
          <Tabs defaultValue="overview" className="w-full h-full">
            <div className="w-full max-w-6xl mx-auto mb-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">
                  {t("statistics.overview")}
                </TabsTrigger>
                <TabsTrigger value="calendar">
                  {t("statistics.calendar")}
                </TabsTrigger>
                <TabsTrigger value="actions">
                  {t("statistics.actions")}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview">
              {renderOverviewContent()}
            </TabsContent>

            <TabsContent value="calendar">
              <div className="w-full max-w-6xl mx-auto space-y-6">
                <GameHistoryCalendar language={language} />
              </div>
            </TabsContent>

            <TabsContent value="actions">
              {renderActionsContent()}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </GameLayout>
  )
} 